import { loadStripe } from "@stripe/stripe-js";
import BaseExpressPay from "../base/express.js";

class StripeExpressPay extends BaseExpressPay {

  /*
    Class to handle Stripe Express Checkout with Apple Pay and Google Pay integration.
    It includes logic to restrict shipping addresses to mainland UK only, excluding Scottish Highlands, Channel Islands and Northern Ireland.
    Test adddesses are:
     - Scottish test address: 1 High Street, Thurso, KW14 7AA
     - Channel Islands test address: 10 Castle Street, St Helier, Jersey, JE2 3RT
     - Northern Ireland test address: 5 High Street, Belfast, BT1 2AA
     - UK mainland test address: 6 Pancras Square, London, N1C 4AG
    Test product variants:
     - https://www.cosmetify.local/chloe-nomade-eau-de-parfum/
     - https://www.cosmetify.local/jo-malone-london-nectarine-blossom-honey-eau-de-parfum/
     - https://www.cosmetify.local/elemis-pro-collagen-night-cream/?vf=3720813
     - https://www.cosmetify.local/estee-lauder-double-wear-stay-in-place-foundation-makeup-spf10/?vf=4844216
  */

  constructor(publicKey, affiliateId, productVariantId, shippingOptions, paymentDisplayName, productVariantPrice) {
    super();

    this.debug = true;

    this.publicKey = publicKey;
    this.stripe = null;
    this.elements = null;
    this.shippingOptionId = null;
    this.expressCheckoutElement = null;

    this.affiliateId = affiliateId;
    this.productVariantId = productVariantId;
    this.paymentDisplayName = paymentDisplayName || "Cosmetify";
    this.shippingOptions = shippingOptions || [];

    this.baseAmount = productVariantPrice;
    this.defaultShippingAmount = this.shippingOptions.length > 0 ? this.shippingOptions[0].amount : 0;
  }

  async init() {
    /*
      Initializes the Stripe instance and sets up the payment request
    */

    try {
      this.stripe = await loadStripe(this.publicKey);
    } catch (err) {
      (this.debug) && console.error("Stripe failed to load.", err);
      return false;
    }

    if (!this.stripe) {
      (this.debug) && console.error("Stripe element is null.");
      return false;
    }

    this.elements = this.stripe.elements({
      mode: "payment",
      amount: this.baseAmount + this.defaultShippingAmount,
      currency: "gbp",
      captureMethod: 'manual',
      setupFutureUsage: 'on_session', 
    });

    return true;
  }

  async createExpressPayButton(container) {
    /*
      Creates and mounts the Express Checkout Element and sets up event handlers
      Returns: true if at least one wallet is available, false otherwise
    */
    if (!this.elements) {
      (this.debug) && console.error("Elements is not initialized.");
      return false;
    }

    const paymentMethods = {
      applePay: "auto",      // always | never | auto
      googlePay: "auto",     // always | never | auto
      link: "never",         // never | auto
      paypal: "never",       // never | auto
      amazonPay: "never",    // never | auto
      klarna: "never",       // never | auto
    };

    this.expressCheckoutElement = this.elements.create("expressCheckout", {
      name: this.paymentDisplayName,
      paymentMethods: paymentMethods,
      emailRequired: true,
      phoneNumberRequired: true,
      billingAddressRequired: true,
      shippingAddressRequired: true,
      buttonType: {
        applePay: 'check-out',  // add-money, book, buy, check-out, contribute, donate, order, plain, reload, rent, subscribe, support, tip, top-up
        googlePay: 'checkout',  // book, buy, checkout, donate, order, pay, plain, subscribe
        paypal: 'checkout',     // paypal, checkout, buynow, pay
      },
      lineItems: [
        {
          name: this.paymentDisplayName, // This shows in the payment sheet
          amount: this.baseAmount,                // £45.00 in cents
        },
        {
          name: 'Shipping',
          amount: this.defaultShippingAmount,
        }
      ],
    });

    return await new Promise((resolve) => {
      this.expressCheckoutElement.on("ready", (event) => {
        const available = !!(event.availablePaymentMethods && Object.keys(event.availablePaymentMethods).length > 0);
        resolve(available);
      });
      this.expressCheckoutElement.on("shippingaddresschange", this.shippingAddressChangeHandler.bind(this));
      this.expressCheckoutElement.on("shippingratechange", this.shippingRateChangeHandler.bind(this));
      this.expressCheckoutElement.on("confirm", this.confirmHandler.bind(this));
      this.expressCheckoutElement.mount(container);
    });
  }


  shippingAddressChangeHandler(event) {
    /*
      Handles shipping address changes and updates available shipping options
        - Validates the shipping address against allowed countries and postal code restrictions
        - If the address is invalid, it returns an error and prevents checkout
        - If the address is valid, it updates the shipping options based on the new address
      To reject the address, call event.resolve() with:
        validationError: { field: 'shipping_address', message: 'Custom error message for the user' }
      To accept the address and update shipping options, call event.resolve() with shippingRates
    */

    const shippingAddress = event.address || {};
    const postalCode = shippingAddress.postal_code || "";
    const country = shippingAddress.country || "";

    const availableShippingOptions = this.getAvailableShippingOptions(postalCode, country);

    if (!availableShippingOptions.length) {
      event.resolve({
        validationError: {
          field: 'shipping_address',
          message: 'We do not ship to this country.',
        },
        shippingRates: [
          {
            id: 'no-shipping',
            displayName: 'Shipping unavailable',
            amount: 0,
          },
        ],
      });
    }
    else {
      const shippingRates = availableShippingOptions.map(option => ({
        id: option.id,
        amount: option.amount,
        displayName: option.label,
        deliveryEstimate: option.detail,
      }));

      event.resolve({
        shippingRates: shippingRates,
      });
    }
  }

  shippingRateChangeHandler(event) {
    /*
      Handles shipping option changes and updates the total amount
    */

    const newName = event.shippingRate.displayName || this.paymentDisplayName;
    const shippingAmount = event.shippingRate ? Math.round(Number(event.shippingRate.amount)) : 0;
    const newTotal = this.baseAmount + shippingAmount;

    this.elements.update({ amount: newTotal });

    event.resolve({
      lineItems: [
        {
          name: newName,
          amount: newTotal,
        }
      ],
    });
  }

  async confirmHandler(event) {
    /*
      Handles the payment method event and processes the payment
      To fail the payment, throw an exception, which will be caught and call event.paymentFailed() with: 
        reason: 'fail' | 'invalid_shipping_address' | 'invalid_billing_address' | 'address_unserviceable'
        message: 'Custom error message for the user'
      To complete the payment, call event.complete("success") or event.complete("fail") based on the outcome
    */

    (this.debug) && console.log("confirm event:", event);

    const expressPaymentType = event.expressPaymentType || "stripe_express_checkout";
    const elementType = event.elementType || "expressCheckout";

    const billingDetails = event.billingDetails || {};

    const shippingRate = event.shippingRate || {};
    const shippingAddress = event.shippingAddress || {};

    const shippingCountry = shippingAddress.address?.country || "";
    const shippingPostalCode = shippingAddress.address?.postal_code || "";
    const countryId = this.getCountryId(shippingPostalCode, shippingCountry);

    const payerEmail = billingDetails.email;
    const payerName = this.splitName(billingDetails.name || shippingAddress.name ||  "");
    const payerPhone = billingDetails.phone || shippingAddress.phone ||  "";

    try {

      if (!payerEmail) {
        throw new Error("Invalid email address. Please provide a valid email to proceed.");
      }

      if (!payerPhone) {
        throw new Error("Invalid phone number. Please provide a valid phone number to proceed.");
      }

      if(!this.validateShippingAddress(shippingCountry, countryId, shippingPostalCode)) {
        throw new Error("Invalid shipping address. Please provide a valid address to proceed.");
      }

      const basketUpdateResponse = await this.updateBasket({ product_variant_id: this.productVariantId });
      if (!basketUpdateResponse.success) {
        throw new Error("Failed to update basket");
      }

      const deliveryOk = await this.setDeliveryInfoOnOrder({ country: countryId, [this.affiliateId]: this.shippingOptionId });
      if (!deliveryOk) {
        throw new Error("Failed to set delivery information");
      }

      const basketOk = await this.basketChecker(basketUpdateResponse.order_id, [this.productVariantId]);
      if (!basketOk) {
        throw new Error("A problem occurred with your basket");
      }

      const paymentIntentPayload = {
        email: payerEmail,
        firstName: payerName.firstName,
        lastName: payerName.lastName,
        phoneNumber: payerPhone,
        billingDetails: billingDetails,
        shippingRate: shippingRate,
        shippingAddress: shippingAddress,
      };

      const paymentIntentResult = await this.generatePaymentIntent(basketUpdateResponse.order_id, paymentIntentPayload, 'product');
      if (paymentIntentResult.error || !paymentIntentResult.client_secret) {
        throw new Error("Failed to generate payment intent");
      }

      console.log("Payment intent result:", paymentIntentResult);

      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: paymentIntentResult.client_secret,
        confirmParams: {
          // Return_url is STILL required for methods that MUST redirect
          // Must direct occurs for standard card payments to handle 3D Secure authentication
          return_url: `${window.location.protocol}//${window.location.host}/checkout/confirmation/`,
        },
        // CRITICAL: Prevents automatic redirect for standard card payments
        redirect: 'if_required', 
      });

      console.log("error:", error);
      console.log("paymentIntent:", paymentIntent);

      if (error) {
        throw new Error(error.message || "Payment confirmation failed");
      }
      
      if (paymentIntent && paymentIntent.status === "requires_capture") {
        // REDIRECT MANUALLY HERE
        // Now that you have the successful Intent, you can choose where to go
        console.log("Payment succeeded, redirecting to success page.");
        throw new Error(error.message || "Payment confirmation failed");

        window.location.href = `${window.location.protocol}//${window.location.host}/order-success?id=${paymentIntent.id}`;
      }

      // throw new Error("Checkout is currently unavailable. Please try again later.");

      // const confirmResult = await this.stripe.confirmCardPayment(paymentIntentResult.client_secret, { payment_method: event.paymentMethod.id });
      // if (confirmResult.error) {
      //   throw new Error(confirmResult.error.message || "Payment confirmation failed");
      // }


      // if (confirmResult.paymentIntent && confirmResult.paymentIntent.status === "requires_capture") {
      //   const processOrderPayload = {
      //     order_id: basketUpdateResponse.order_id,
      //     payment_intent_client_secret: paymentIntentResult.client_secret,
      //     FirstName: payerName.firstName,
      //     LastName: payerName.lastName,
      //     Email: payerEmail,
      //     PhoneNumber: payerPhone,
      //     ShipAddress1: (shippingAddress.addressLine && shippingAddress.addressLine[0]) || shippingAddress.line1 || "",
      //     ShipAddress2: (shippingAddress.addressLine && shippingAddress.addressLine[1]) || shippingAddress.line2 || "",
      //     ShipCounty: shippingAddress.region || shippingAddress.state || "",
      //     ShipPostcode: shippingAddress.postalCode || shippingAddress.postal_code || "",
      //     ShipCountry: shippingAddress.country || "",
      //     BillAddress1: billingDetails.address.line1,
      //     BillAddress2: billingDetails.address.line2,
      //     BillCounty: billingDetails.address.state,
      //     BillPostcode: billingDetails.address.postal_code,
      //     BillCountry: countryId,
      //     complete: true,
      //   };

      //   const processOrderResult = await this.processOrder(processOrderPayload);
      //   if (processOrderResult.startsWith("error:")) {
      //     throw new Error("A problem occurred when processing your order.");
      //   }

      // } else {
      //   throw new Error("Payment was not successful.");
      // }

    }
    catch (err) {
      event.paymentFailed({
        reason: 'fail',
        message: err.message || "An error occurred during payment. Please try again.",
      });
      return;
    }
  }
}

export default StripeExpressPay;
